import { wrapCreateBrowserRouter } from '@sentry/react';
import { isIE, isOpera } from 'react-device-detect';
import { BrowserRouterProps, createBrowserRouter, Outlet } from 'react-router-dom';
import { PUBLIC_URL } from '../env';
import {
  Administration,
  Analytics,
  BookmarksFeed,
  CardDetails,
  Channels,
  ChannelSettings,
  Chats,
  CheckEmail,
  CommentsFeed,
  CommentsFeedRightSide,
  EditProfile,
  EventDetails,
  EventsFeed,
  EventsFeedRightSide,
  Forgot,
  ForgotSet,
  FrontDoorTab,
  Integrations,
  LeaderBoard,
  Login,
  MyTopics,
  NewsFeed,
  NewsFeedAdmin,
  NewsFeedAdminRightSide,
  Newsletters,
  NoAccess,
  NotFound,
  Notifications,
  NotificationsRightSide,
  NotSupported,
  PinnedStoryFeedItems,
  ProfileFeedItems,
  ProfileMe,
  ProfileMeAdmin,
  ProfileOther,
  ProfileOtherAdmin,
  ProfileRightSide,
  PushNotifications,
  Responsive,
  SearchFeed,
  SearchFeedAdmin,
  SearchFeedAdminRightSide,
  Settings,
  Signup,
  StoryFeedItems,
  StoryFeedItemsAdmin,
  StoryFeedItemsAdminRightSide,
  StoryTabList,
  StoryTabListAdmin,
  StoryTabListAdminRightSide,
  TagsFeed,
  TagsFeedAdmin,
  TagsFeedAdminRightSide,
  UnhandledError,
  UserDirectory,
  UserDirectoryRightSide,
  UserManagement,
  Users,
  Vendor,
} from '../pages';

import { DetailsContent, DetailsContentAdmin, RightSide } from '../components';
import { AdminLayout, FormLayout, OrganisationLayout, ReaderLayout } from '../layouts';
import { optArrItem, StoryTabType } from '../shared';

const browserProps: BrowserRouterProps = {
  basename: PUBLIC_URL,
};

const isDeprecatedBrowser = isIE || isOpera;

// * READER LAYOUT
const withRightSideReader = {
  path: '/',
  element: <ReaderLayout rightSideModule={<RightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/',
      element: <NewsFeed />,
    },
    {
      path: '/storyTab1',
      element: <StoryTabList storyTabType={StoryTabType.storyTab1} />,
    },
    {
      path: '/storyTab2',
      element: <StoryTabList storyTabType={StoryTabType.storyTab2} />,
    },
    {
      path: '/storyTab3',
      element: <StoryTabList storyTabType={StoryTabType.storyTab3} />,
    },
    {
      path: '/storyTab4',
      element: <StoryTabList storyTabType={StoryTabType.storyTab4} />,
    },
    {
      path: '/storyTab5',
      element: <StoryTabList storyTabType={StoryTabType.storyTab5} />,
    },
    {
      path: '/pinnedStoryTab/:storyId/items',
      element: <PinnedStoryFeedItems />,
    },
    {
      path: '/pinnedStoryTab2/:storyId/items',
      element: <PinnedStoryFeedItems />,
    },
    {
      path: '/pinnedStoryTab3/:storyId/items',
      element: <PinnedStoryFeedItems />,
    },
    {
      path: '/pinnedStoryTab4/:storyId/items',
      element: <PinnedStoryFeedItems />,
    },
    {
      path: '/pinnedStoryTab5/:storyId/items',
      element: <PinnedStoryFeedItems />,
    },
    {
      path: '/frontDoorTab:tabIndex',
      element: <FrontDoorTab />,
    },
    {
      path: '/tags/:organisationTagId',
      element: <TagsFeed />,
    },
    // * default path for story feed items
    {
      path: '/:storyTab/:storyId/items',
      element: <StoryFeedItems />,
    },
    {
      path: '/events',
      element: <EventsFeed />,
    },
  ],
};

const withoutRightSideReader = {
  path: '/',
  element: <ReaderLayout />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/search',
      element: <SearchFeed />,
    },
    {
      path: '/details/:storyId/:cardId',
      element: <CardDetails ContentComponent={DetailsContent} />,
    },
    {
      path: '/profile/edit',
      element: <EditProfile />,
    },
    {
      path: '/pushNotifications',
      element: <PushNotifications />,
    },
    {
      path: '/newsletters',
      element: <Newsletters />,
    },
    {
      path: '/bookmarks',
      element: <BookmarksFeed />,
    },
    {
      path: '/eventDetails/:eventId',
      element: <EventDetails />,
    },
  ],
};

const fullWidthContentReader = {
  path: '/',
  element: <ReaderLayout fullWidthContent />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/chats',
      element: <Chats />,
    },
    {
      path: '/leaderBoard',
      element: <LeaderBoard />,
    },
  ],
};

const profileReader = {
  path: '/',
  element: <ReaderLayout rightSideModule={<ProfileRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/profile',
      element: <ProfileMe />,
    },
    {
      path: '/profile/:profileId',
      element: <ProfileOther />,
    },
  ],
};

const profileItemsReader = {
  path: '/',
  element: <ReaderLayout rightSideModule={<ProfileRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/profile/items',
      element: <ProfileFeedItems />,
    },
    {
      path: '/profile/:profileId/items',
      element: <ProfileFeedItems />,
    },
  ],
};

const userDirectoryReader = {
  path: '/',
  element: <ReaderLayout rightSideModule={<UserDirectoryRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/userDirectory',
      element: <UserDirectory />,
    },
  ],
};

const myTopicsReader = {
  path: '/',
  element: <ReaderLayout />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/myTopics',
      element: <MyTopics />,
    },
  ],
};

// * ADMIN LAYOUT
const multiFeedsAdmin = {
  path: '/',
  element: <AdminLayout rightSideModule={<NewsFeedAdminRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin',
      element: <NewsFeedAdmin />,
    },
  ],
};

const storyTabAdmin = {
  path: '/',
  element: <AdminLayout rightSideModule={<StoryTabListAdminRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/storyTab',
      element: <StoryTabListAdmin />,
    },
  ],
};

const storyTab1Admin = {
  path: '/',
  element: (
    <AdminLayout
      rightSideModule={<StoryTabListAdminRightSide storyTabType={StoryTabType.storyTab1} />}
    />
  ),
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/storyTab1',
      element: <StoryTabListAdmin storyTabType={StoryTabType.storyTab1} />,
    },
  ],
};

const storyTab2Admin = {
  path: '/',
  element: (
    <AdminLayout
      rightSideModule={<StoryTabListAdminRightSide storyTabType={StoryTabType.storyTab2} />}
    />
  ),
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/storyTab2',
      element: <StoryTabListAdmin storyTabType={StoryTabType.storyTab2} />,
    },
  ],
};

const storyTab3Admin = {
  path: '/',
  element: (
    <AdminLayout
      rightSideModule={<StoryTabListAdminRightSide storyTabType={StoryTabType.storyTab3} />}
    />
  ),
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/storyTab3',
      element: <StoryTabListAdmin storyTabType={StoryTabType.storyTab3} />,
    },
  ],
};

const storyTab4Admin = {
  path: '/',
  element: (
    <AdminLayout
      rightSideModule={<StoryTabListAdminRightSide storyTabType={StoryTabType.storyTab4} />}
    />
  ),
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/storyTab4',
      element: <StoryTabListAdmin storyTabType={StoryTabType.storyTab4} />,
    },
  ],
};

const storyTab5Admin = {
  path: '/',
  element: (
    <AdminLayout
      rightSideModule={<StoryTabListAdminRightSide storyTabType={StoryTabType.storyTab5} />}
    />
  ),
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/storyTab5',
      element: <StoryTabListAdmin storyTabType={StoryTabType.storyTab5} />,
    },
  ],
};

const pinnedStoryTabAdmin = {
  path: '/',
  element: (
    <AdminLayout
      rightSideModule={<StoryTabListAdminRightSide storyTabType={StoryTabType.pinnedStoryTab} />}
    />
  ),
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/pinnedStoryTab',
      element: <StoryTabListAdmin storyTabType={StoryTabType.pinnedStoryTab} />,
    },
  ],
};

const pinnedStoryTab2Admin = {
  path: '/',
  element: (
    <AdminLayout
      rightSideModule={<StoryTabListAdminRightSide storyTabType={StoryTabType.pinnedStoryTab2} />}
    />
  ),
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/pinnedStoryTab2',
      element: <StoryTabListAdmin storyTabType={StoryTabType.pinnedStoryTab2} />,
    },
  ],
};

const pinnedStoryTab3Admin = {
  path: '/',
  element: (
    <AdminLayout
      rightSideModule={<StoryTabListAdminRightSide storyTabType={StoryTabType.pinnedStoryTab3} />}
    />
  ),
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/pinnedStoryTab3',
      element: <StoryTabListAdmin storyTabType={StoryTabType.pinnedStoryTab3} />,
    },
  ],
};

const pinnedStoryTab4Admin = {
  path: '/',
  element: (
    <AdminLayout
      rightSideModule={<StoryTabListAdminRightSide storyTabType={StoryTabType.pinnedStoryTab4} />}
    />
  ),
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/pinnedStoryTab4',
      element: <StoryTabListAdmin storyTabType={StoryTabType.pinnedStoryTab4} />,
    },
  ],
};

const pinnedStoryTab5Admin = {
  path: '/',
  element: (
    <AdminLayout
      rightSideModule={<StoryTabListAdminRightSide storyTabType={StoryTabType.pinnedStoryTab5} />}
    />
  ),
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/pinnedStoryTab5',
      element: <StoryTabListAdmin storyTabType={StoryTabType.pinnedStoryTab5} />,
    },
  ],
};

const storyFeedItemsAdmin = {
  path: '/',
  element: <AdminLayout rightSideModule={<StoryFeedItemsAdminRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/storyTab1/:storyId/items',
      element: <StoryFeedItemsAdmin storyTabType={StoryTabType.storyTab1} />,
    },
    {
      path: '/admin/storyTab2/:storyId/items',
      element: <StoryFeedItemsAdmin storyTabType={StoryTabType.storyTab2} />,
    },
    {
      path: '/admin/storyTab3/:storyId/items',
      element: <StoryFeedItemsAdmin storyTabType={StoryTabType.storyTab3} />,
    },
    {
      path: '/admin/storyTab4/:storyId/items',
      element: <StoryFeedItemsAdmin storyTabType={StoryTabType.storyTab4} />,
    },
    {
      path: '/admin/storyTab5/:storyId/items',
      element: <StoryFeedItemsAdmin storyTabType={StoryTabType.storyTab5} />,
    },
    {
      path: '/admin/pinnedStoryTab/:storyId/items',
      element: <StoryFeedItemsAdmin storyTabType={StoryTabType.pinnedStoryTab} />,
    },
    {
      path: '/admin/pinnedStoryTab2/:storyId/items',
      element: <StoryFeedItemsAdmin storyTabType={StoryTabType.pinnedStoryTab2} />,
    },
    {
      path: '/admin/pinnedStoryTab3/:storyId/items',
      element: <StoryFeedItemsAdmin storyTabType={StoryTabType.pinnedStoryTab3} />,
    },
    {
      path: '/admin/pinnedStoryTab4/:storyId/items',
      element: <StoryFeedItemsAdmin storyTabType={StoryTabType.pinnedStoryTab4} />,
    },
    {
      path: '/admin/pinnedStoryTab5/:storyId/items',
      element: <StoryFeedItemsAdmin storyTabType={StoryTabType.pinnedStoryTab5} />,
    },
    {
      path: '/admin/storyTab/:storyId/items',
      element: <StoryFeedItemsAdmin />,
    },
  ],
};

const tagsFeedAdmin = {
  path: '/',
  element: <AdminLayout rightSideModule={<TagsFeedAdminRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/tags/:organisationTagId',
      element: <TagsFeedAdmin />,
    },
  ],
};

const profileAdmin = {
  path: '/',
  element: <AdminLayout rightSideModule={<ProfileRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/profile',
      element: <ProfileMeAdmin />,
    },
    {
      path: '/admin/profile/:profileId',
      element: <ProfileOtherAdmin />,
    },
  ],
};

const profileItemsAdmin = {
  path: '/',
  element: <AdminLayout rightSideModule={<ProfileRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/profile/items',
      element: <ProfileFeedItems />,
    },
    {
      path: '/admin/profile/:profileId/items',
      element: <ProfileFeedItems />,
    },
  ],
};

const searchAdmin = {
  path: '/',
  element: <AdminLayout rightSideModule={<SearchFeedAdminRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/search',
      element: <SearchFeedAdmin />,
    },
  ],
};

const notificationsAdmin = {
  path: '/',
  element: <AdminLayout rightSideModule={<NotificationsRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/notifications',
      element: <Notifications />,
    },
  ],
};

const eventsFeedAdmin = {
  path: '/',
  element: <AdminLayout rightSideModule={<EventsFeedRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/events',
      element: <EventsFeed />,
    },
  ],
};

const commentsFeedAdmin = {
  path: '/',
  element: <AdminLayout rightSideModule={<CommentsFeedRightSide />} />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/comments',
      element: <CommentsFeed />,
    },
  ],
};

const withoutRightSideAdmin = {
  path: '/',
  element: <AdminLayout />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/details/:storyId/:cardId',
      element: <CardDetails ContentComponent={DetailsContentAdmin} />,
    },
    {
      path: '/admin/profile/edit',
      element: <EditProfile />,
    },
    {
      path: '/admin/pushNotifications',
      element: <PushNotifications />,
    },
    {
      path: '/admin/newsletters',
      element: <Newsletters />,
    },
    {
      path: '/admin/channelSettings',
      element: <ChannelSettings />,
    },
    {
      path: '/admin/eventDetails/:eventId',
      element: <EventDetails />,
    },
  ],
};

const fullWidthContentAdmin = {
  path: '/',
  element: <AdminLayout fullWidthContent />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/admin/chats',
      element: <Chats />,
    },
    {
      path: '/admin/leaderBoard',
      element: <LeaderBoard />,
    },
    {
      path: '/admin/userManagement',
      element: <UserManagement />,
    },
    {
      path: '/admin/integrations',
      element: <Integrations />,
    },
  ],
};

// * FORM LAYOUT
const auth = {
  path: '/',
  element: <FormLayout />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/auth/login',
      element: <Login />,
    },
    {
      path: '/auth/forgotPasswordSet',
      element: <ForgotSet />,
    },
    {
      path: '/auth/signup',
      element: <Signup />,
    },
    {
      path: '/auth/forgot',
      element: <Forgot />,
    },
    {
      path: '/auth/checkEmail',
      element: <CheckEmail />,
    },
  ],
};

const noAccess = {
  path: '/',
  element: <FormLayout />,
  children: [
    {
      path: '/noAccess',
      element: <NoAccess />,
    },
  ],
};

const notSupported = {
  path: '*',
  element: <FormLayout />,
  children: [
    {
      path: '*',
      element: <NotSupported />,
    },
  ],
};

const notFound = {
  path: '*',
  element: <FormLayout />,
  children: [
    {
      path: '*',
      element: <NotFound />,
    },
  ],
};

// * ORG LAYOUT
const orgDashboard = {
  path: '/',
  element: <OrganisationLayout />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/organisation',
      element: <Channels />,
    },
    {
      path: '/organisation/administration',
      element: <Administration />,
    },
    {
      path: '/organisation/users',
      element: <Users />,
    },
    {
      path: '/organisation/notifications',
      element: <Notifications isOrgLevel />,
    },
    {
      path: '/organisation/analytics',
      element: <Analytics />,
    },
    {
      path: '/organisation/settings',
      element: <Settings />,
    },
  ],
};

// * NO LAYOUT
const vendor = {
  path: '/',
  element: <Outlet />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/vendor',
      element: <Vendor />,
    },
    // {
    //   path: '/welcome',
    //   element: <Welcome />,
    // },
  ],
};

// * DEV ONLY
const devOnly = {
  path: '/',
  element: <Outlet />,
  errorElement: <UnhandledError />,
  children: [
    {
      path: '/dev/responsive',
      element: <Responsive />,
    },
  ],
};

export const router = wrapCreateBrowserRouter(createBrowserRouter)(
  isDeprecatedBrowser
    ? [notSupported]
    : [
        // * READER LAYOUT
        withRightSideReader,
        withoutRightSideReader,
        fullWidthContentReader,
        profileReader,
        profileItemsReader,
        userDirectoryReader,
        myTopicsReader,
        // * ADMIN LAYOUT
        multiFeedsAdmin,
        storyTabAdmin,
        storyTab1Admin,
        storyTab2Admin,
        storyTab3Admin,
        storyTab4Admin,
        storyTab5Admin,
        pinnedStoryTabAdmin,
        pinnedStoryTab2Admin,
        pinnedStoryTab3Admin,
        pinnedStoryTab4Admin,
        pinnedStoryTab5Admin,
        storyFeedItemsAdmin,
        tagsFeedAdmin,
        eventsFeedAdmin,
        commentsFeedAdmin,
        withoutRightSideAdmin,
        fullWidthContentAdmin,
        profileAdmin,
        profileItemsAdmin,
        searchAdmin,
        notificationsAdmin,
        // * FORM LAYOUT
        auth,
        notFound,
        noAccess,
        // * ORG LAYOUT
        orgDashboard,
        // * NO LAYOUT
        vendor,
        // * DEV ONLY
        ...optArrItem(process.env.NODE_ENV !== 'production', devOnly),
      ],
  browserProps
);
